import React, { useState, useEffect, useRef } from 'react';
import api from './api/api';
import UploadForm from './components/UploadForm';
import VideoList from './components/VideoList';
import VideoSettings from './components/VideoSettings';
import ChannelPointsSettings from './components/ChannelPointsSettings';
import LoginForm from './components/LoginForm';
import StreamingSidebar from './components/StreamingSidebar';
import UserInterface from './components/UserInterface';
import { Container, Button, Alert, Navbar, Nav, Form, Spinner } from 'react-bootstrap';
import './App.css';
import Cookies from 'js-cookie';

function CookieBanner({ darkMode, onAccept }) {
  return (
    <div
      role="dialog"
      aria-label="Cookie-Hinweis"
      className="cookie-banner"
      style={{
        backgroundColor: darkMode ? '#212529' : '#f8f9fa',
        borderTop: `2px solid ${darkMode ? '#495057' : '#dee2e6'}`,
      }}
    >
      <div className="d-flex flex-wrap align-items-center justify-content-between gap-3">
        <div className={darkMode ? 'text-light' : 'text-dark'} style={{ maxWidth: '800px' }}>
          <strong>🍪 Cookie-Hinweis</strong>
          <p className="mb-1 mt-1" style={{ fontSize: '0.9rem' }}>
            Diese Anwendung verwendet Cookies, die für den Betrieb notwendig sind und nicht deaktiviert werden können:
          </p>
          <ul className="mb-1" style={{ fontSize: '0.85rem', paddingLeft: '1.2rem' }}>
            <li><strong>Session-Cookie</strong> – speichert deine Anmeldung (wird beim Schließen des Browsers gelöscht, außer „Angemeldet bleiben" ist aktiviert)</li>
            <li><strong>Theme-Cookie</strong> – merkt sich deine Hell-/Dunkel-Modus-Einstellung (365 Tage)</li>
          </ul>
          <small className={darkMode ? 'text-secondary' : 'text-muted'}>
            Es werden keine Tracking- oder Werbe-Cookies verwendet.
          </small>
        </div>
        <Button variant="primary" onClick={onAccept} style={{ whiteSpace: 'nowrap' }}>
          Verstanden
        </Button>
      </div>
    </div>
  );
}

function App() {
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  const [loading, setLoading] = useState(true);
  const [logoutMessage, setLogoutMessage] = useState('');
  const [darkMode, setDarkMode] = useState(false);
  const [view, setView] = useState('upload'); // 'upload' | 'manage' | 'settings' | 'users'
  const [cookieConsent, setCookieConsent] = useState(() => {
    return localStorage.getItem('cookieConsent') === 'accepted';
  });

  const videoListRef = useRef();

  const checkLogin = async () => {
    try {
      const response = await api.get('/session');
      setIsLoggedIn(response.data.logged_in === true);
      setIsAdmin(response.data.is_admin === true);
    } catch {
      setIsLoggedIn(false);
      setIsAdmin(false);
    } finally {
      setLoading(false);
    }
  };

  const refreshVideoList = () => {
    if (videoListRef.current) {
      videoListRef.current.fetchVideos();
    }
  };

  useEffect(() => {
    const savedTheme = Cookies.get('theme');
    if (savedTheme === 'dark') {
      setDarkMode(true);
    }
    checkLogin();
  }, []);

  // Bootstrap-Theme global setzen, damit auch Modals (Portal), Formularfelder,
  // ListGroups, Alerts und text-muted im Darkmode korrekt gefärbt werden.
  useEffect(() => {
    document.documentElement.setAttribute('data-bs-theme', darkMode ? 'dark' : 'light');
    document.body.style.backgroundColor = darkMode ? '#212529' : '#f8f9fa';
  }, [darkMode]);

  const handleLogout = async () => {
    try {
      await api.post('/logout');
      setIsLoggedIn(false);
      setView('upload');
      setLogoutMessage('✅ Erfolgreich ausgeloggt.');
      setTimeout(() => setLogoutMessage(''), 5000);
    } catch (err) {
      console.error('Logout fehlgeschlagen:', err);
    }
  };

  const toggleDarkMode = () => {
    const newDarkMode = !darkMode;
    setDarkMode(newDarkMode);
    Cookies.set('theme', newDarkMode ? 'dark' : 'light', { expires: 365 });
  };

  const handleCookieAccept = () => {
    localStorage.setItem('cookieConsent', 'accepted');
    setCookieConsent(true);
  };

  const mainBgClass = darkMode ? 'bg-dark text-light' : 'bg-light text-dark';
  const cardBgClass = darkMode ? 'bg-secondary text-light' : 'bg-white text-dark';

  if (loading) {
    return (
      <Container className="mt-5 text-center" role="status">
        <Spinner animation="border" variant="primary" />
        <div className="mt-2 text-muted">Lade …</div>
      </Container>
    );
  }

  if (!isLoggedIn) {
    return (
      <div className={`min-vh-100 ${mainBgClass}`}>
        <Container className="mt-5">
          {logoutMessage && <Alert variant="success">{logoutMessage}</Alert>}

          <div className="d-flex justify-content-end align-items-center mb-3">
            <span className="me-2" style={{ fontSize: '1.5rem' }}>
              {darkMode ? '🌙' : '☀️'}
            </span>
            <Form.Check
              type="switch"
              id="dark-mode-switch-login"
              aria-label="Dunkelmodus"
              checked={darkMode}
              onChange={toggleDarkMode}
              label=""
            />
          </div>

          <LoginForm onLogin={checkLogin} darkMode={darkMode} cardBgClass={cardBgClass} />
        </Container>

        {!cookieConsent && <CookieBanner darkMode={darkMode} onAccept={handleCookieAccept} />}
      </div>
    );
  }

  return (
    <div className={`min-vh-100 ${mainBgClass}`}>
      <Navbar
        expand="lg"
        sticky="top"
        bg={darkMode ? 'dark' : 'light'}
        variant={darkMode ? 'dark' : 'light'}
        className="app-navbar px-3 mb-4"
      >
        <Navbar.Brand className="fw-semibold">🎬 StreamSmith</Navbar.Brand>
        <Navbar.Toggle aria-controls="main-nav" />
        <Navbar.Collapse id="main-nav">
          <Nav className="me-auto gap-lg-1" activeKey={view} onSelect={(k) => k && setView(k)}>
            <Nav.Link eventKey="upload" active={view === 'upload'}>📤 Upload</Nav.Link>
            <Nav.Link eventKey="manage" active={view === 'manage'}>🗂️ Verwalten</Nav.Link>
            <Nav.Link eventKey="settings" active={view === 'settings'}>⚙️ Einstellungen</Nav.Link>
            <Nav.Link eventKey="users" active={view === 'users'}>👥 Benutzer</Nav.Link>
          </Nav>
          <div className="d-flex align-items-center gap-2 mt-2 mt-lg-0">
            <span aria-hidden="true" style={{ fontSize: '1.25rem' }}>{darkMode ? '🌙' : '☀️'}</span>
            <Form.Check
              type="switch"
              id="dark-mode-switch"
              aria-label="Dunkelmodus"
              checked={darkMode}
              onChange={toggleDarkMode}
            />
            <Button onClick={handleLogout} size="sm" variant={darkMode ? 'outline-light' : 'outline-dark'} className="ms-2">
              Logout
            </Button>
          </div>
        </Navbar.Collapse>
      </Navbar>

      <Container>
        <div className="row">
          <div className="col-lg-9 col-md-8 mb-4">
            {view === 'upload' && (
              <UploadForm
                darkMode={darkMode}
                cardBgClass={cardBgClass}
                onVideoSaved={refreshVideoList}
              />
            )}
            {view === 'manage' && (
              <VideoList
                darkMode={darkMode}
                cardBgClass={cardBgClass}
                ref={videoListRef}
              />
            )}

            {view === 'users' && (
              <UserInterface
                darkMode={darkMode}
                cardBgClass={cardBgClass}
                isAdmin={isAdmin}
              />
            )}

            {view === 'settings' && (
              <>
                <VideoSettings
                  darkMode={darkMode}
                  cardBgClass={cardBgClass}
                />
                <ChannelPointsSettings
                  darkMode={darkMode}
                  cardBgClass={cardBgClass}
                />
              </>
            )}
          </div>

          <div className="col-lg-3 col-md-4">
            <StreamingSidebar darkMode={darkMode} cardBgClass={cardBgClass} />
          </div>
        </div>
      </Container>

      {!cookieConsent && <CookieBanner darkMode={darkMode} onAccept={handleCookieAccept} />}
    </div>
  );
}

export default App;